export interface ErrorEntry {
  ts: string;
  level: 'error' | 'warn' | 'unhandled';
  msg: string;
  url?: string;
  stack?: string;
  route?: string;
}

const MAX_ENTRIES = 100;
const MSG_MAX = 500;
const STACK_MAX = 1000;

const buffer: ErrorEntry[] = [];

function truncate(s: string | undefined, max: number): string | undefined {
  if (s == null) return undefined;
  return s.length > max ? s.slice(0, max) : s;
}

const SECRET_PATTERN = /\b(token|password|api[_\-]?key|authorization|bearer|secret|sk[-_][a-zA-Z0-9]+|pk[-_][a-zA-Z0-9]+)\b[^\s,;]*/gi;

function redact(s: string | undefined): string | undefined {
  if (!s) return s;
  return s.replace(SECRET_PATTERN, '[REDACTED]');
}

// Entries whose message is only decoration (e.g. console.error('='.repeat(80))
// banner lines from api.service.js) waste ring slots and pollute bug reports.
const DECORATION_ONLY = /^[=\-_*#\s.]*$/;

export const errorRingBuffer = {
  push(entry: ErrorEntry): void {
    const msg = truncate(redact(entry.msg), MSG_MAX) ?? '';
    if (DECORATION_ONLY.test(msg)) return;

    const last = buffer[buffer.length - 1];
    if (last && last.level === entry.level && last.msg === msg && last.stack === entry.stack) {
      last.ts = entry.ts;
      return;
    }

    const sanitized: ErrorEntry = {
      ts: entry.ts,
      level: entry.level,
      msg,
      url: entry.url,
      stack: truncate(redact(entry.stack), STACK_MAX),
      route: entry.route ?? (typeof window !== 'undefined' ? window.location.pathname : undefined),
    };
    buffer.push(sanitized);
    if (buffer.length > MAX_ENTRIES) {
      buffer.splice(0, buffer.length - MAX_ENTRIES);
    }
  },

  /** Returns a copy of the buffer without clearing it. Safe to call from render. */
  snapshot(): ErrorEntry[] {
    return [...buffer];
  },

  /** Returns a copy of the buffer AND clears it. Use when consuming logs for submission. */
  drain(): ErrorEntry[] {
    const copy = [...buffer];
    buffer.splice(0, buffer.length);
    return copy;
  },

  /** Clears the buffer without returning its contents. */
  clear(): void {
    buffer.splice(0, buffer.length);
  },

  /** Counts entries within the given window where level is 'error' or 'unhandled'. */
  recentErrorCount(windowMs = 30000): number {
    const cutoff = Date.now() - windowMs;
    return buffer.filter(
      (e) =>
        (e.level === 'error' || e.level === 'unhandled') &&
        new Date(e.ts).getTime() >= cutoff
    ).length;
  },

  lastErrorAt(): string | null {
    for (let i = buffer.length - 1; i >= 0; i--) {
      if (buffer[i].level === 'error' || buffer[i].level === 'unhandled') {
        return buffer[i].ts;
      }
    }
    return null;
  },

  hasRecentError(windowMs = 30000): boolean {
    const last = errorRingBuffer.lastErrorAt();
    if (!last) return false;
    return Date.now() - new Date(last).getTime() < windowMs;
  },
};
